// ============================================================
// Charts — alle rein in SVG, ohne Bibliothek
// ============================================================

import type { CSSProperties, ReactNode } from 'react';
import { CO } from '../theme';
import { eur, fmtNum, sEur } from '../format';
import type { GroupRow, MonthRow } from '../types';

// ---------- niceTicks ----------
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (min === max) max = min + 1;
  const range = max - min;
  const step0 = range / (count || 4);
  const mag = Math.pow(10, Math.floor(Math.log10(step0)));
  const norm = step0 / mag;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

// ---------- SVG-Helfer ----------
interface SvgBoxProps {
  vw: number;
  vh: number;
  children: ReactNode;
  style?: CSSProperties;
}
export function SvgBox({ vw, vh, children, style }: SvgBoxProps) {
  return (
    <svg
      viewBox={`0 0 ${vw} ${vh}`}
      width="100%"
      style={{ display: 'block', fontFamily: "'IBM Plex Sans',sans-serif", ...style }}
      preserveAspectRatio="xMidYMid meet"
    >
      {children}
    </svg>
  );
}

interface GridYProps {
  x0: number;
  x1: number;
  yFn: (v: number) => number;
  ticks: number[];
  unit?: string;
}
export function GridY({ x0, x1, yFn, ticks, unit }: GridYProps) {
  return (
    <>
      {ticks.map((t, i) => (
        <g key={`g${i}`}>
          <line
            x1={x0}
            x2={x1}
            y1={yFn(t)}
            y2={yFn(t)}
            stroke={t === 0 ? '#b9bfb2' : CO.border}
            strokeWidth={t === 0 ? 1.4 : 1}
          />
          <text
            x={x0 - 8}
            y={yFn(t) + 3.5}
            textAnchor="end"
            fontSize={11}
            fill={CO.faint}
            className="num"
          >
            {unit === '€' ? fmtNum(t) : t}
            {unit && unit !== '€' ? unit : ''}
          </text>
        </g>
      ))}
    </>
  );
}

export function ChartEmpty({ height = 220 }: { height?: number }) {
  return (
    <div
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: CO.faint,
        fontSize: 14,
        border: `1px dashed ${CO.border}`,
        borderRadius: 10,
        background: '#fafbf9',
      }}
    >
      Keine Daten im gewählten Zeitraum.
    </div>
  );
}

// ---------- FinanceChart (gruppiert Einsatz/Auszahlung + Netto-Linie) ----------
interface FinanceChartProps {
  rows: MonthRow[];
  prevRows: MonthRow[] | null;
  colorEinsatz: string;
  colorAuszahlung: string;
}
export function FinanceChart({ rows, prevRows, colorEinsatz, colorAuszahlung }: FinanceChartProps) {
  if (!rows.length) return <ChartEmpty />;
  const vw = Math.max(720, rows.length * 74 + 90);
  const vh = 320;
  const padL = 56, padR = 18, padT = 18, padB = 46;
  const nettos = rows.map((r) => r.netto);
  const maxBar = Math.max(1, ...rows.map((r) => Math.max(r.einsatz, r.gewinn)), ...nettos);
  const minV = Math.min(0, ...nettos);
  const ticks = niceTicks(minV, maxBar, 5);
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const yFn = (v: number) => padT + ((yMax - v) / (yMax - yMin)) * (vh - padT - padB);
  const bandW = (vw - padL - padR) / rows.length;
  const bw = Math.min(20, bandW * 0.28);

  const bars = rows.map((r, i) => {
    const cx = padL + bandW * i + bandW / 2;
    const y0 = yFn(0);
    return (
      <g key={`b${i}`}>
        <rect
          x={cx - bw - 3}
          y={yFn(r.einsatz)}
          width={bw}
          height={Math.max(0, y0 - yFn(r.einsatz))}
          fill={colorEinsatz}
          rx={2}
        >
          <title>{`${r.label} — Einsatz ${eur(r.einsatz)}`}</title>
        </rect>
        <rect
          x={cx + 3}
          y={yFn(r.gewinn)}
          width={bw}
          height={Math.max(0, y0 - yFn(r.gewinn))}
          fill={colorAuszahlung}
          rx={2}
        >
          <title>{`${r.label} — Auszahlung ${eur(r.gewinn)}`}</title>
        </rect>
        <text x={cx} y={vh - padB + 16} textAnchor="middle" fontSize={11} fill={CO.muted}>
          {r.label}
        </text>
      </g>
    );
  });

  const linePts = rows.map((r, i) => [padL + bandW * i + bandW / 2, yFn(r.netto)] as [number, number]);
  const lineD = linePts.map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1]).join(' ');
  const dots = linePts.map((p, i) => (
    <circle
      key={`d${i}`}
      cx={p[0]}
      cy={p[1]}
      r={3.4}
      fill="#fff"
      stroke={rows[i].netto >= 0 ? CO.pos : CO.neg}
      strokeWidth={2}
    >
      <title>{`${rows[i].label} — Nettoergebnis ${sEur(rows[i].netto)}`}</title>
    </circle>
  ));

  let prevLine: ReactNode = null;
  if (prevRows && prevRows.length === rows.length) {
    const pd = prevRows
      .map((r, i) => (i ? 'L' : 'M') + (padL + bandW * i + bandW / 2) + ' ' + yFn(r.netto))
      .join(' ');
    prevLine = <path d={pd} fill="none" stroke="#b9bfb2" strokeWidth={1.6} strokeDasharray="4 4" />;
  }

  return (
    <SvgBox vw={vw} vh={vh}>
      <GridY x0={padL} x1={vw - padR} yFn={yFn} ticks={ticks} unit="€" />
      {prevLine}
      {bars}
      <path d={lineD} fill="none" stroke="#3a3f37" strokeWidth={2} />
      {dots}
    </SvgBox>
  );
}

// ---------- TwoSeriesBars (Einsatz vs Auszahlung, zeitverlauf) ----------
interface TwoSeriesBarsProps {
  rows: GroupRow[];
  colorEinsatz: string;
  colorAuszahlung: string;
}
export function TwoSeriesBars({ rows, colorEinsatz, colorAuszahlung }: TwoSeriesBarsProps) {
  if (!rows.length) return <ChartEmpty />;
  const vw = Math.max(720, rows.length * (rows.length > 40 ? 20 : 56) + 80);
  const vh = 300;
  const padL = 56, padR = 16, padT = 16, padB = 46;
  const maxV = Math.max(1, ...rows.map((r) => Math.max(r.einsatz, r.gewinn)));
  const ticks = niceTicks(0, maxV, 5);
  const yMax = ticks[ticks.length - 1];
  const yFn = (v: number) => padT + ((yMax - v) / yMax) * (vh - padT - padB);
  const bandW = (vw - padL - padR) / rows.length;
  const bw = Math.min(16, Math.max(3, bandW * 0.32));
  const showLbl = rows.length <= 24;

  const bars = rows.map((r, i) => {
    const cx = padL + bandW * i + bandW / 2;
    const y0 = yFn(0);
    return (
      <g key={i}>
        <rect
          x={cx - bw - 1.5}
          y={yFn(r.einsatz)}
          width={bw}
          height={Math.max(0, y0 - yFn(r.einsatz))}
          fill={colorEinsatz}
          rx={1.5}
        >
          <title>{`${r.label} — Einsatz ${eur(r.einsatz)}`}</title>
        </rect>
        <rect
          x={cx + 1.5}
          y={yFn(r.gewinn)}
          width={bw}
          height={Math.max(0, y0 - yFn(r.gewinn))}
          fill={colorAuszahlung}
          rx={1.5}
        >
          <title>{`${r.label} — Auszahlung ${eur(r.gewinn)}`}</title>
        </rect>
        {showLbl ? (
          <text x={cx} y={vh - padB + 15} textAnchor="middle" fontSize={10.5} fill={CO.muted}>
            {r.label}
          </text>
        ) : null}
      </g>
    );
  });

  return (
    <SvgBox vw={vw} vh={vh}>
      <GridY x0={padL} x1={vw - padR} yFn={yFn} ticks={ticks} unit="€" />
      {bars}
    </SvgBox>
  );
}

// ---------- CumulativeChart (kumuliertes Nettoergebnis) ----------
interface CumulativeChartProps {
  entries: { iso: string; gewinn: number; einsatz: number }[];
}
export function CumulativeChart({ entries }: CumulativeChartProps) {
  const sorted = entries.slice().sort((a, b) => (a.iso < b.iso ? -1 : 1));
  if (!sorted.length) return <ChartEmpty />;
  let run = 0;
  const pts = sorted.map((e) => {
    run += e.gewinn - e.einsatz;
    return { iso: e.iso, v: run };
  });
  const vw = 960, vh = 300;
  const padL = 62, padR = 20, padT = 16, padB = 40;
  const vals = pts.map((p) => p.v);
  const ticks = niceTicks(Math.min(0, ...vals), Math.max(0, ...vals), 5);
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const yFn = (v: number) => padT + ((yMax - v) / (yMax - yMin)) * (vh - padT - padB);
  const t0 = new Date(sorted[0].iso).getTime();
  const t1 = new Date(sorted[sorted.length - 1].iso).getTime();
  const xFn = (iso: string) =>
    padL + (t1 === t0 ? 0.5 : (new Date(iso).getTime() - t0) / (t1 - t0)) * (vw - padL - padR);
  const d = pts.map((p, i) => (i ? 'L' : 'M') + xFn(p.iso).toFixed(1) + ' ' + yFn(p.v).toFixed(1)).join(' ');
  const area =
    d +
    ' L ' +
    xFn(pts[pts.length - 1].iso).toFixed(1) +
    ' ' +
    yFn(0) +
    ' L ' +
    xFn(pts[0].iso).toFixed(1) +
    ' ' +
    yFn(0) +
    ' Z';

  const nlab = Math.min(6, pts.length);
  const xlabs: ReactNode[] = [];
  for (let i = 0; i < nlab; i++) {
    const idx = Math.round((i * (pts.length - 1)) / Math.max(1, nlab - 1));
    const p = pts[idx];
    const dd = new Date(p.iso);
    const ds = `${String(dd.getDate()).padStart(2, '0')}.${String(dd.getMonth() + 1).padStart(2, '0')}`;
    xlabs.push(
      <text key={`x${i}`} x={xFn(p.iso)} y={vh - padB + 16} textAnchor="middle" fontSize={10.5} fill={CO.muted}>
        {ds}.{dd.getFullYear()}
      </text>,
    );
  }

  return (
    <SvgBox vw={vw} vh={vh}>
      <GridY x0={padL} x1={vw - padR} yFn={yFn} ticks={ticks} unit="€" />
      <path d={area} fill="rgba(194,59,52,.08)" />
      <path d={d} fill="none" stroke={CO.neg} strokeWidth={2} />
      {xlabs}
      <circle
        cx={xFn(pts[pts.length - 1].iso)}
        cy={yFn(pts[pts.length - 1].v)}
        r={4}
        fill={CO.neg}
      />
    </SvgBox>
  );
}

// ---------- DivergingChart (positive & negative Monatswerte) ----------
interface DivergingChartProps {
  rows: { label: string; netto: number }[];
}
export function DivergingChart({ rows }: DivergingChartProps) {
  if (!rows.length) return <ChartEmpty />;
  const vw = Math.max(720, rows.length * 66 + 80);
  const vh = 280;
  const padL = 56, padR = 16, padT = 14, padB = 44;
  const vals = rows.map((r) => r.netto);
  const ticks = niceTicks(Math.min(0, ...vals), Math.max(0, ...vals), 5);
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const yFn = (v: number) => padT + ((yMax - v) / (yMax - yMin)) * (vh - padT - padB);
  const bandW = (vw - padL - padR) / rows.length;
  const bw = Math.min(30, bandW * 0.5);

  const bars = rows.map((r, i) => {
    const cx = padL + bandW * i + bandW / 2;
    const y0 = yFn(0);
    const yv = yFn(r.netto);
    return (
      <g key={i}>
        <rect
          x={cx - bw / 2}
          y={Math.min(y0, yv)}
          width={bw}
          height={Math.max(1, Math.abs(yv - y0))}
          fill={r.netto >= 0 ? CO.pos : CO.neg}
          rx={2}
          opacity={0.9}
        >
          <title>{`${r.label} — Netto ${sEur(r.netto)}`}</title>
        </rect>
        <text x={cx} y={vh - padB + 15} textAnchor="middle" fontSize={10.5} fill={CO.muted}>
          {r.label}
        </text>
      </g>
    );
  });

  return (
    <SvgBox vw={vw} vh={vh}>
      <GridY x0={padL} x1={vw - padR} yFn={yFn} ticks={ticks} unit="€" />
      {bars}
    </SvgBox>
  );
}

// ---------- HBarChart (horizontale Balken mit Label/Wert) ----------
interface HBarRow {
  label: string;
  v: number;
  /** Optional: Wenn vorangestellt, wird genutzt. Andernfalls wird ein Anteil angezeigt. */
  pctv?: number;
}
interface HBarChartProps {
  rows: HBarRow[];
  colorFn: (r: HBarRow) => string;
  valFmt?: (v: number) => string;
}
export function HBarChart({ rows, colorFn, valFmt }: HBarChartProps) {
  if (!rows.length) return <ChartEmpty height={120} />;
  const max = Math.max(1, ...rows.map((r) => r.v));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((r, i) => {
        const pctOfMax = (r.v / max) * 100;
        const displayVal = valFmt ? valFmt(r.v) : eur(r.v);
        return (
          <div
            key={i}
            style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px', alignItems: 'center', gap: 12 }}
          >
            <div
              style={{
                fontSize: 13,
                color: CO.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={r.label}
            >
              {r.label}
            </div>
            <div style={{ background: CO.border2, borderRadius: 5, height: 18, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${pctOfMax}%`,
                  height: '100%',
                  background: colorFn(r),
                  borderRadius: 5,
                  transition: 'width .3s ease',
                }}
              />
            </div>
            <div
              className="num"
              style={{ textAlign: 'right', fontSize: 13, color: CO.text, fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              {displayVal}
              {typeof r.pctv === 'number' ? (
                <span style={{ marginLeft: 6, color: CO.faint, fontWeight: 400, fontSize: 11.5 }}>
                  ({r.pctv.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %)
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- DivergingHBars (mittelsenkrechte Null-Linie, Netto je Spielart) ----------
interface DivergingHBarsProps {
  rows: { label: string; netto: number }[];
}
export function DivergingHBars({ rows }: DivergingHBarsProps) {
  if (!rows.length) return <ChartEmpty height={120} />;
  const maxAbs = Math.max(1, ...rows.map((a) => Math.abs(a.netto)));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((a, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 100px', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.label}</div>
          <div style={{ position: 'relative', height: 20, background: CO.border2, borderRadius: 5 }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#b9bfb2' }} />
            <div
              style={{
                position: 'absolute',
                top: 2,
                bottom: 2,
                borderRadius: 4,
                background: a.netto >= 0 ? CO.pos : CO.neg,
                left: a.netto >= 0 ? '50%' : `${50 - (Math.abs(a.netto) / maxAbs) * 50}%`,
                width: `${(Math.abs(a.netto) / maxAbs) * 50}%`,
              }}
            />
          </div>
          <div
            className="num"
            style={{
              textAlign: 'right',
              fontSize: 13,
              fontWeight: 600,
              color: a.netto >= 0 ? CO.pos : CO.neg,
            }}
          >
            {sEur(a.netto)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Legend ----------
interface LegendItem {
  l: string;
  c: string;
  line?: boolean;
  dash?: boolean;
}
export function Legend({ items }: { items: LegendItem[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginTop: 6 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: CO.muted }}>
          {it.dash ? (
            <span style={{ width: 16, height: 0, borderTop: `2px dashed ${it.c}` }} />
          ) : (
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: it.line ? 6 : 3,
                background: it.c,
                display: 'inline-block',
              }}
            />
          )}
          {it.l}
        </div>
      ))}
    </div>
  );
}
